import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'wouter';
import {
  Target,
  Heart,
  Zap,
  Users,
  Shield,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LandingLayout } from './landing/shared/LandingLayout';
import {
  generateCanonicalUrl,
  combineSchemas,
  generateOrganizationSchema,
  generateWebPageSchema,
  generateBreadcrumbSchema,
} from '@/utils/seo';

const SITE_URL = 'https://slotyapp.com';

export default function AboutPage() {
  const { t } = useTranslation(['about', 'common']);

  // SEO structured data
  const structuredData = combineSchemas(
    generateOrganizationSchema(),
    generateWebPageSchema({
      name: 'About Sloty - Our Mission & Story',
      description: t('about:hero.subtitle'),
      url: generateCanonicalUrl('/about'),
    }),
    generateBreadcrumbSchema([
      { name: 'Home', url: SITE_URL },
      { name: 'About', url: `${SITE_URL}/about` },
    ])
  );

  const values = [
    {
      icon: Target,
      title: t('about:values.items.0.title'),
      description: t('about:values.items.0.description'),
    },
    {
      icon: Heart,
      title: t('about:values.items.1.title'),
      description: t('about:values.items.1.description'),
    },
    {
      icon: Zap,
      title: t('about:values.items.2.title'),
      description: t('about:values.items.2.description'),
    },
    {
      icon: Users,
      title: t('about:values.items.3.title'),
      description: t('about:values.items.3.description'),
    },
    {
      icon: Shield,
      title: t('about:values.items.4.title'),
      description: t('about:values.items.4.description'),
    },
    {
      icon: Sparkles,
      title: t('about:values.items.5.title'),
      description: t('about:values.items.5.description'),
    },
  ];

  return (
    <LandingLayout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{`${t('about:seo.title')} | Sloty`}</title>
        <meta name="title" content={`${t('about:seo.title')} | Sloty`} />
        <meta name="description" content={t('about:seo.description')} />
        <meta name="keywords" content="sloty, about us, booking software company, appointment scheduling, service business software" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/about`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:title" content={t('about:seo.title')} />
        <meta property="og:description" content={t('about:seo.description')} />
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta property="og:site_name" content="Sloty" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('about:seo.title')} />
        <meta name="twitter:description" content={t('about:seo.description')} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            {t('about:hero.title')}
          </h1>
          <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed">
            {t('about:hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('about:mission.title')}
            </h2>
          </div>
          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <p className="text-lg leading-relaxed">
              {t('about:mission.paragraph1')}
            </p>
            <p className="text-lg leading-relaxed">
              {t('about:mission.paragraph2')}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('about:values.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('about:values.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-brand-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('about:story.title')}
            </h2>
          </div>
          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <p className="text-lg leading-relaxed">
              {t('about:story.paragraph1')}
            </p>
            <p className="text-lg leading-relaxed">
              {t('about:story.paragraph2')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-brand-600 to-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t('about:cta.title')}
          </h2>
          <p className="text-xl text-brand-100 mb-8">
            {t('about:cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="bg-white text-brand-600 hover:bg-brand-50 px-8">
                {t('about:cta.primary')}
              </Button>
            </Link>
            <a href="/#pricing">
              <Button size="lg" variant="ghost" className="bg-white/10 text-white border border-white/30 hover:bg-white/20 hover:text-white px-8">
                {t('about:cta.secondary')}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
